import React from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS as dndCSS } from '@dnd-kit/utilities';
import styles from './DraggableAndDroppable.module.scss'
import cn from 'classnames'

// Минимальное расстояние в пикселях для определения начала drag-жеста на touch-устройствах
// Используется для различения между кликом и началом перетаскивания
const TOUCH_MOVE_THRESHOLD_PX = 8;

interface Props {
  id: any,
  idActiveDraggable: any,
  children?: React.ReactNode,
  onClick?: (e: any) => void,
  className?: string,
  dragEnabled?: boolean,
}

export const DraggableAndDroppable = ({
  id,
  idActiveDraggable,
  children,
  onClick,
  className,
  dragEnabled = true,
}: Props) => {
  const touchStateRef = React.useRef({ startX: 0, startY: 0, pointerId: null as number | null, moved: false })
  const suppressClickRef = React.useRef(false)

  // Draggable
  const { attributes, listeners, setNodeRef: setNodeDraggableRef, transform } = useDraggable({
    id,
  });

  // Droppable
  const { isOver, setNodeRef: setNodeDroppableRef } = useDroppable({
    id,
  });

  const styleDraggable = {
    // Outputs `translate3d(x, y, 0)`
    transform: dndCSS.Translate.toString(transform),
  };

  const dragAttributes = dragEnabled ? attributes : {}
  const dragListeners = dragEnabled ? listeners : {}

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') return
    touchStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      pointerId: event.pointerId,
      moved: false,
    }
    suppressClickRef.current = false
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') return
    if (touchStateRef.current.pointerId !== event.pointerId) return
    const deltaX = Math.abs(event.clientX - touchStateRef.current.startX)
    const deltaY = Math.abs(event.clientY - touchStateRef.current.startY)
    if (deltaY > TOUCH_MOVE_THRESHOLD_PX && deltaY > deltaX) {
      touchStateRef.current.moved = true
      suppressClickRef.current = true
    }
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') return
    if (touchStateRef.current.pointerId !== event.pointerId) return
    suppressClickRef.current = touchStateRef.current.moved
    touchStateRef.current.pointerId = null
  }

  const handlePointerCancel = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') return
    if (touchStateRef.current.pointerId !== event.pointerId) return
    suppressClickRef.current = true
    touchStateRef.current.pointerId = null
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (suppressClickRef.current) {
      event.preventDefault()
      event.stopPropagation()
      suppressClickRef.current = false
      return
    }
    onClick?.(event)
  }

  return (
    <div
      className={cn(
        styles.droppable,
        idActiveDraggable && styles.isDraggableSome,
        (isOver && idActiveDraggable !== id) && styles.isOver,
        idActiveDraggable === id && styles.isDraggableActiveCurrent
      )}
      ref={setNodeDroppableRef}
    >
      <button
        className={cn(
          styles.draggable,
          className,
        )}
        ref={setNodeDraggableRef}
        style={styleDraggable}
        {...dragListeners}
        {...dragAttributes}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {children}
      </button>
    </div>
  )
}

export default DraggableAndDroppable;
